import { Text, useToast } from '@chakra-ui/react';
import { useState } from 'react';
import FormModal from '../../../components/FormModal';
import FormField from '../../../components/fields/FormField';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import { useAdminAdjustWallet } from '../hooks/useAdminAdjustWallet';
import { computeNewBalance, isWalletAdjustmentValid } from '../utils/wallet.utils';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  currentBalance: number;
};

export default function AdjustWalletModal({ isOpen, onClose, userId, currentBalance }: Props) {
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState('');
  const toast = useToast();
  const mutation = useAdminAdjustWallet();

  const newBalance = computeNewBalance(currentBalance, amount);
  const isValid = isWalletAdjustmentValid(amount, reason, currentBalance);

  const handleSubmit = async () => {
    try {
      const result = await mutation.mutateAsync({ userId, amount, reason });
      toast({
        title: translate('wallet_admin.adjust_success'),
        description: `${result.previousBalance.toFixed(2)} → ${result.newBalance.toFixed(2)}`,
        status: 'success',
        duration: 4000,
      });
      setAmount(0);
      setReason('');
      onClose();
    } catch (error: any) {
      toast({
        title: translate('wallet_admin.adjust_error'),
        description: error?.message || translate('common.unknown_error'),
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleClose = () => {
    setAmount(0);
    setReason('');
    onClose();
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={translate('wallet_admin.adjust_balance')}
      onSubmit={handleSubmit}
      submitLabel={translate('wallet_admin.confirm_adjust')}
      cancelLabel={translate('common.cancel')}
      isSubmitting={mutation.isLoading}
      isSubmitDisabled={!isValid}
    >
      <FormStack>
        <FormField
          type="number"
          label={translate('wallet_admin.amount')}
          value={amount}
          onChange={(val) => setAmount(isNaN(val) ? 0 : val)}
          step={1}
          helperText={translate('wallet_admin.amount_hint')}
          numberInputProps={{ precision: 2 }}
        />

        <FormField
          type="textarea"
          label={translate('wallet_admin.reason')}
          value={reason}
          onChange={setReason}
          placeholder={translate('wallet_admin.reason_placeholder')}
          textareaProps={{ maxLength: 255 }}
        />

        <Text fontSize="sm" fontWeight="bold" mt={4}>
          {translate('wallet_admin.preview')}: {currentBalance.toFixed(2)} → {newBalance.toFixed(2)}
        </Text>
        {newBalance < 0 && (
          <Text fontSize="sm" color="red.500">
            {translate('wallet_admin.debit_exceeds_balance')}
          </Text>
        )}
      </FormStack>
    </FormModal>
  );
}
